import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { AssignableRole, Member, Organization } from '@/api/types'
import { i18n } from '@/i18n'
import { authKeys } from './auth'

export const organizationKeys = {
  all: ['organization'] as const,
  detail: ['organization', 'detail'] as const,
  members: ['organization', 'members'] as const,
}

export const organizationQuery = () =>
  queryOptions({
    queryKey: organizationKeys.detail,
    queryFn: ({ signal }) => api.get<Organization>('/organization', { signal }),
  })

export function useOrganization() {
  return useQuery(organizationQuery())
}

export const membersQuery = () =>
  queryOptions({
    queryKey: organizationKeys.members,
    queryFn: ({ signal }) => api.get<Member[]>('/organization/members', { signal }),
  })

export function useMembers() {
  return useQuery(membersQuery())
}

/* ---------- members (Area C) ---------- */

export function useChangeMemberRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: AssignableRole }) =>
      api.patch<Member>(`/organization/members/${id}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: organizationKeys.members }),
    meta: { success: i18n.t('team:toasts.roleChanged') },
  })
}

export function useRemoveMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/organization/members/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: organizationKeys.members }),
    meta: { success: i18n.t('team:toasts.removed') },
  })
}

/** Leaving the organisation: the session loses access, so the caller signs out or switches. */
export function useLeaveOrganization() {
  return useMutation({
    mutationFn: (membershipId: string) => api.delete(`/organization/members/${membershipId}`),
  })
}

export function useTransferOwnership() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) => api.post<Member[]>('/organization/transfer-ownership', { memberId }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: organizationKeys.members })
      await qc.invalidateQueries({ queryKey: authKeys.session })
    },
    meta: { success: i18n.t('team:toasts.transferred') },
  })
}
